import { EntityType } from "../enums/entity-type";

export class SectionDefinition {
  id: number;
  name: string = '';
  attributeDefinitions: AttributeDefinition[] = [];
  assetType: SectionDefinitionEntityType;
  displayOrder: number = 0;
  substationTemplateId?: number;

  constructor(data?: any) {
    this.id = data.id;
    this.name = data.name;
    this.assetType = data.entityType;
    this.attributeDefinitions = (data.attributeDefinitions ?? []).map(
      (attr: any) => new AttributeDefinition(attr)
    );
    this.displayOrder = data.displayOrder;
    this.substationTemplateId = data.substationTemplateId ?? null;
    return this;
  }
}

export class SectionInstance {
  sectionTitle!: string;
  sectionDefinitionId!: number;
  attributeValues: AttributeValue[] = [];
  expanded: boolean = true;
}

export class AttributeValue {
  name?: string;
  type!: keyof FormTypes;
  stringValue?: string;
  doubleValue?: number;
  boolValue?: boolean;
  dateValue?: string;
  is24HoursFormat?: boolean;
  latitude?: number;
  longitude?: number;
  physicalAddress?: string;
  lastUpdateTime?: string;
  selectedOptions?: string[];
  attributeDefinitionId!: number;
  isRequired?: boolean;
  maxNumberValue?: number;
  regularExpression?: string;
  placeholderText?: string;
  is24HourFormat?: boolean;
  isSingleType?: boolean;
  selectedOptionIds?: number[];
  optionDefinitions?: OptionDefinition[];
}

// Values edited on the details page, keyed by attributeDefinitionId.
export type EditedAttributeValues = Record<number, number | string | number[] | null>;

export class SectionDefinitionFilter {
  sortBy: SortTypeSecDef = SortTypeSecDef.NEWEST;
  search: string = '';
  filterBy: FilterPropertyKey = FilterPropertyKey.NAME;
}

export enum FilterPropertyKey {
  NAME = 1,
  ASSET_TYPE = 2,
}

export enum FilterProperty {
  NAME = 'Name',
  ASSET_TYPE = 'Asset Type',
}

export enum SortTypeSecDef {
  NEWEST = 1,
  OLDEST = 2,
  DEVICE_NAME_ASC = 3,
  DEVICE_NAME_DESC = 4,
  DISPLAY_ORDER = 5,
  DISPLAY_ORDER_DESC = 6,
}

export class AddNewSectionDefinitionDto {
  id: number = 0;
  name: string = '';
  attributeDefinitions: AttributeDefinition[] = [];
  assetType?: SectionDefinitionEntityType;
  multipleDevice: boolean = false;
  displayOrder: number = 0;
  substationTemplateId?: number;
  equipmentType?: number;

  constructor(data?: any) {
    if (data) {
      this.id = data.id;
      this.name = data.name;
      this.assetType = data.assetType;
      this.attributeDefinitions = (data.attributeDefinitions ?? []).map(
        (attr: any) => new AttributeDefinition(attr)
      );
      this.multipleDevice = data.multipleDevice;
      this.displayOrder = data.displayOrder;
      this.substationTemplateId = data.substationTemplateId ?? null;
      this.equipmentType = data.equipmentType ?? null;
    }

    return this;
  }
}

export class AttributeDefinition {
  id: number = 0;
  label: string = '';
  placeholderText: string = '';
  fieldType?: FormTypes;
  isRequired: boolean = true;
  defaultState: boolean = true;
  minNumberValue?: number | null;
  maxNumberValue?: number | null;
  displayOrder: number = 0;
  is24HoursFormat?: boolean;
  isSingleType?: boolean = true;
  optionDefinitions: OptionDefinition[] = [];
  locationType?: LocationTypes;
  scanType?: ScanTypes;
  imageType?: ImageTypes;
  parentAttributeId?: number;
  parentAttribute?: AttributeDefinition;
  fileExtensionTypes?: FileExtensionTypes[] = [];
  guid: string = crypto.randomUUID();
  isEnabled?: boolean = true;
  regularExpression?: string;
  excludedVoltages?: VoltageClass[] = [];
  constructor(data?: any) {
    if (data) {
      this.label = data.label;
      this.fieldType = data.fieldType;
      this.isRequired = data.isRequired;
      this.minNumberValue = data.minNumberValue;
      this.maxNumberValue = data.maxNumberValue;
      this.displayOrder = data.displayOrder;
      this.optionDefinitions = (data.optionDefinitions ?? []).map(
        (opt: any) => new OptionDefinition(opt)
      );
      this.locationType = data.locationType;
      this.scanType = data.scanType;
      this.id = data.id;
      this.isSingleType = data.isSingleType;
      this.fileExtensionTypes = data.fileExtensionTypes;
      this.defaultState = data.defaultState;
      this.placeholderText = data.placeholderText;
      this.is24HoursFormat = data.is24HoursFormat;
      this.isEnabled = data.isEnabled;
      this.parentAttributeId = data.parentAttributeId;
      this.regularExpression = data.regularExpression;
      this.imageType = data.imageType;
      this.excludedVoltages = data.excludedVoltages ?? [];
    }
  }
}

export class OptionDefinition {
  id: number = 0;
  value: string = '';
  displayOrder: number = 0;
  parentOptionId?: number;
  parentOption?: OptionDefinition;

  constructor(data?: any) {
    this.id = data.id;
    this.value = data.value;
    this.displayOrder = data.displayOrder;
    this.parentOption = data.parentOption;
    this.parentOptionId = data.parentOptionId;
    return this;
  }
}

export enum SectionDefinitionEntityType {
  Pole = 0,
  DistributionSubstation = 1,
  MvPole = 2,
  InjectionSubstation = 3,
  HvPole = 4,
  TransmissionStation = 5,
  Building = 9,
  SubstationTemplate = 11,
  EquipmentTemplate = 12,
  NewCustomer = 13,
  NewMeterRequest = 14,
  MeterUpgrade = 15,
  MeterSeparationSplit = 16,
  DedicatedDistributionTransformerRequest = 17,
  DedicatedFeederRequest = 18,
  LVPoleExtension = 19,
  MVPoleExtension = 20,
  VoluntaryDisconnection = 21,
  PermanentDisconnection = 22,
  Customer = 23,
}

export enum FormTypes {
  Text = 1,
  Number = 2,
  Bool = 3,
  Dropdown = 4,
  ConditionalDropdown = 5,
  ChildDropdown = 6,
  DateTime = 7,
  Location = 8,
  Scan = 9,
  SingleSelect = 10,
  MultiSelect = 11,
  TextDescription = 12,
  File = 13,
  Image = 14,
}

export enum VoltageClass {
  LV = 1,
  MV = 2,
  HV = 3,
}

export enum LocationTypes {
  Coordinates = 1,
  PostalCode = 2,
  Both = 3,
}

export enum ScanTypes {
  QRCode = 1,
  BarCode = 2,
  Both = 3,
}

export enum ImageTypes {
  Camera = 1,
  File = 2,
  Both = 3,
}

export enum FileExtensionTypes {
  JPG = 1,
  JPEG = 2,
  PNG = 3,
  MP4 = 4,
  DOC = 5,
  DOCX = 6,
  PDF = 7,
  CSV = 8,
}
